import { db, auth } from '../../../../firebaseConfig';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  serverTimestamp,
  addDoc,
  writeBatch,
} from 'firebase/firestore';

// Create a notification document for a user
export const createNotification = async ({ userId, type, actorId, projectId = null, postId = null, message = '' }) => {
  if (!auth?.currentUser) throw new Error('No authenticated user');
  if (!userId || !type) throw new Error('Missing notification fields');
  const notificationsRef = collection(db, 'notifications');
  const payload = {
    userId,
    type,            // 'project_invite' | 'project_invite_accepted' | 'project_launched' | ...
    actorId,         // who triggered this notification
    projectId: projectId || null,
    postId: postId || null,
    message: message || '',
    read: false,
    createdAt: serverTimestamp(),
  };
  await addDoc(notificationsRef, payload);
};

/**
 * Link a post to a project after launch
 * @deprecated This function is NOT used in the FromPostbox flow. 
 * Projects created via FromPostbox are standalone and do not create/link posts.
 * This function is kept for backward compatibility with FromPost and FromSolution flows.
 */
export const linkPostToProject = async (postId, projectId) => {
  if (!auth?.currentUser) throw new Error('No authenticated user');
  if (!postId || !projectId) throw new Error('Missing postId or projectId');
  const postRef = doc(db, 'posts', postId);
  await updateDoc(postRef, { projectId, hasProject: true });
};

// Create a membership record for a user who is part of a project (e.g., creator at launch)
export const createProjectMembership = async (projectId, userId, role = 'Member') => {
  if (!auth?.currentUser) throw new Error('No authenticated user');
  if (!projectId || !userId) throw new Error('Missing projectId or userId');
  const membersRef = collection(db, 'project_members');
  const payload = {
    projectId,
    userId,
    role: role || 'Member',
    joinedAt: serverTimestamp(),
  };
  await addDoc(membersRef, payload);
};

// Create invites for a list of users (excluding the creator)
// invitees: Array<{ userId: string, role?: string, username?: string, photoURL?: string }>
export const createProjectInvites = async (projectId, inviterId, invitees = []) => {
  if (!auth?.currentUser) throw new Error('No authenticated user');
  if (!projectId || !inviterId) throw new Error('Missing projectId or inviterId');
  const invitesRef = collection(db, 'project_invites');

  for (const inv of invitees) {
    const payload = {
      projectId,
      inviterId,
      inviteeId: inv.userId,
      role: inv.role || 'Member',
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await addDoc(invitesRef, payload);

    // Send a notification to the invitee
    try {
      await createNotification({
        userId: inv.userId,
        type: 'project_invite',
        actorId: inviterId,
        projectId,
        message: 'You have been invited to join a project.',
      });
    } catch (e) {
      // Non-fatal for invite flow
      console.warn('Failed to create invite notification:', e);
    }
  }
};

// Accept an invite: mark the invite accepted and create membership.
// Note: Projects update is restricted to creator by rules; do not update the project doc here.
export const acceptProjectInvite = async (inviteId) => {
  if (!auth?.currentUser) throw new Error('No authenticated user');
  if (!inviteId) throw new Error('Missing inviteId');

  const inviteRef = doc(db, 'project_invites', inviteId);
  const snap = await getDoc(inviteRef);
  if (!snap.exists()) throw new Error('Invite not found');
  const invite = snap.data();
  const { currentUser } = auth;
  if (invite.inviteeId !== currentUser.uid) {
    throw new Error('You are not authorized to accept this invite');
  }

  // Batch: update invite status and create membership
  const batch = writeBatch(db);
  batch.update(inviteRef, { status: 'accepted', updatedAt: serverTimestamp() });
  const membersRef = collection(db, 'project_members');
  const newMemberRef = doc(membersRef);
  batch.set(newMemberRef, {
    projectId: invite.projectId,
    userId: currentUser.uid,
    role: invite.role || 'Member',
    joinedAt: serverTimestamp(),
  });
  await batch.commit();

  // Notify inviter that invite was accepted
  try {
    await createNotification({
      userId: invite.inviterId,
      type: 'project_invite_accepted',
      actorId: currentUser.uid,
      projectId: invite.projectId,
      message: 'accepted your project invitation.',
    });
  } catch (e) {
    console.warn('Failed to create acceptance notification:', e);
  }
};

// Decline an invite: set status declined. Do not modify project doc.
export const declineProjectInvite = async (inviteId) => {
  if (!auth?.currentUser) throw new Error('No authenticated user');
  if (!inviteId) throw new Error('Missing inviteId');
  const inviteRef = doc(db, 'project_invites', inviteId);
  const snap = await getDoc(inviteRef);
  if (!snap.exists()) throw new Error('Invite not found');
  const invite = snap.data();
  const { currentUser } = auth;
  if (invite.inviteeId !== currentUser.uid) {
    throw new Error('You are not authorized to decline this invite');
  }
  await updateDoc(inviteRef, { status: 'declined', updatedAt: serverTimestamp() });
};
